import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCirclePause,
  faCircleInfo,
  faCircleMinus,
  faHourglassEnd,
  faSpinner,
  faWrench,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Button } from '@/components/ui/button';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/stores/auth.store';
import { useAccessRestrictionStore } from '@/stores/access-restriction.store';
import {
  ACCESS_RESTRICTION_COPY,
  parseAccessRestrictionReason,
  type AccessRestrictionReason,
} from '@/lib/access-restriction';
import { ROUTES } from '@/routes/routes';

/**
 * The dedicated screen shown when the server has refused this workspace.
 *
 * Replaces what the tenant app used to do with a `403`: let each page render its
 * own "Could not load…" strip, or surface the guard's own sentence
 * (`"Subscription is not active (status: SUSPENDED)"`) verbatim as a toast.
 *
 * Deliberately shows **no** status code, exception name, subscription id,
 * tenant id or diagnostic of any kind. The reason arrives as a validated enum,
 * never as free text from the server, so no backend string can reach the screen
 * even if one changes.
 *
 * The page issues no API calls. That is what keeps it a terminus rather than one
 * half of a redirect cycle — see AccessRestrictionRedirect.
 */

/**
 * Per-state visual. Warning for suspended, expiry for expired, neutral for ended.
 *
 * The glyph is `text-foreground`, not `text-accent-foreground`. `--accent-foreground`
 * is the colour meant to sit on a *solid* accent fill, so at `bg-accent/20` it
 * came out near-black in both themes — legible on the pale light background,
 * effectively invisible on the dark one. `--foreground` inverts with the theme,
 * which is what a glyph on a translucent tint needs.
 */
const REASON_ICON: Record<AccessRestrictionReason, { icon: IconDefinition; cls: string }> = {
  // Paused, not broken — accent rather than destructive red. This is a state the
  // service provider can lift, and the page must not read as a crash.
  suspended: { icon: faCirclePause, cls: 'bg-accent/20 text-foreground ring-accent/40' },
  expired: { icon: faHourglassEnd, cls: 'bg-accent/20 text-foreground ring-accent/40' },
  cancelled: { icon: faCircleMinus, cls: 'bg-muted text-muted-foreground ring-border' },
  generic: { icon: faCircleInfo, cls: 'bg-muted text-muted-foreground ring-border' },
};

export function AccessRestrictedPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const storedReason = useAccessRestrictionStore((s) => s.reason);
  const clearRestriction = useAccessRestrictionStore((s) => s.clear);
  const [signingOut, setSigningOut] = useState(false);

  // Store first — it is what the interceptor just wrote. The `?reason=` param is
  // the refresh fallback only: the store is intentionally not persisted, so a
  // reload would otherwise lose the copy. Either way the value is one of four
  // validated enum members and decides nothing but wording.
  const reason: AccessRestrictionReason =
    storedReason ?? parseAccessRestrictionReason(searchParams.get('reason'));
  const copy = ACCESS_RESTRICTION_COPY[reason];
  const visual = REASON_ICON[reason];

  /**
   * D-05 — signing out revokes the refresh token server-side, not just locally.
   * Mirrors the header's handler: local state is cleared and the user is
   * navigated away regardless of the request's outcome.
   *
   * The restriction flag is cleared here, and only here, so the login page is
   * reachable. Leaving it set would have <AccessRestrictionRedirect> pull the
   * user straight back the moment any tenant route mounted.
   */
  const handleBackToSignIn = async () => {
    setSigningOut(true);
    const { refreshToken, clearAuth } = useAuthStore.getState();
    try {
      if (refreshToken) await authService.logout(refreshToken);
    } catch {
      // Ignore — a revocation that fails must still clear the local session.
    }
    clearAuth();
    clearRestriction();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  return (
    // min-h-screen rather than h-screen: on a narrow viewport the card must be
    // allowed to grow and the page to scroll, instead of clipping the CTA.
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-10 sm:px-6">
      <main className="w-full max-w-md">
        {/* Branding — same mark and wording as the sign-in screen. */}
        <div className="mb-8 flex flex-col items-center gap-3">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-xl ring-2 ring-primary/30"
            style={{ background: '#283618' }}
          >
            <FontAwesomeIcon icon={faWrench} className="h-6 w-6 text-white" />
          </div>
          <span className="text-base font-semibold text-foreground">Service Portal</span>
        </div>

        <div className="rounded-xl border border-border bg-card p-6 text-center shadow-sm sm:p-8">
          <div
            className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ring-1 ${visual.cls}`}
          >
            <FontAwesomeIcon icon={visual.icon} className="h-7 w-7" />
          </div>

          <h1 className="mt-6 text-xl font-semibold text-card-foreground sm:text-2xl">
            {copy.title}
          </h1>

          <p className="mt-3 text-sm leading-relaxed text-card-foreground/80">{copy.message}</p>

          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{copy.secondary}</p>

          {/*
            "Contact Customer Care" is deliberately absent.
            
            There is no configured service-provider support contact to point it
            at: `_app_meta` holds no `platform.support_email` / `_phone` row, no
            SUPPORT_* deployment variable exists, and the tenant's own
            CompanyProfile.supportEmail is its support address for *its* customers
            — the wrong party for a subscription matter, and unreachable here
            besides (that endpoint is behind the very guard that blocked us).
            Inventing an address would be worse than omitting the button, so the
            guidance above names the channel in words instead.
          */}
          <div className="mt-8 flex flex-col gap-3">
            <Button
              type="button"
              onClick={handleBackToSignIn}
              disabled={signingOut}
              className="w-full min-h-[44px]"
            >
              {signingOut ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
                  Signing out…
                </>
              ) : (
                'Back to Sign In'
              )}
            </Button>
          </div>

          {/* Accurate: nothing about the account or its password was changed —
              the restriction is on the workspace, not the credential. */}
          <p className="mt-6 text-xs text-muted-foreground">
            Your account credentials remain unchanged.
          </p>
        </div>
      </main>
    </div>
  );
}
